import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FiArrowRight, FiShoppingBag, FiX } from "react-icons/fi";
import { Button } from "../../ui/Button";
import { useCart } from "../../context/CartContext";
import { useCartDrawer } from "../../context/CartDrawerContext";
import { useToast } from "../../context/ToastContext";
import { formatLek } from "../../Utils/currency";
import * as P from "./Products.styled";

const ProductDetailModalRedesigned = ({ product, onClose }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addItemToCart } = useCart();
  const { peekDrawer } = useCartDrawer();
  const { success: toastSuccess } = useToast();
  const reduceMotion = useReducedMotion();
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!product) return undefined;
    setQuantity(1);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", handleKey);
    };
  }, [product, onClose]);

  const hasStock = Number(product?.stock || 0);
  const isOutOfStock = !product || hasStock === 0;
  const badges = Array.isArray(product?.badges) ? product.badges : [];

  const handleAddToCart = () => {
    if (!product || isOutOfStock) return;
    const added = addItemToCart({
      id: product.id,
      farmId: product.farmId,
      farmName: product.farmName || "",
      name: product.name,
      price: Number(product.price || 0),
      image: product.image,
      unit: product.unit,
      description: product.description,
      quantity,
    });
    if (added) {
      peekDrawer();
      toastSuccess(
        `${product.name} ${t("cart.added", { defaultValue: "added to cart" })}`
      );
    }
    onClose();
  };

  const handleViewFull = () => {
    if (!product) return;
    onClose();
    navigate(`/products/${product.id}`);
  };

  return (
    <AnimatePresence>
      {product ? (
        <P.ModalOverlay
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          role="presentation"
        >
          <P.ModalFrame
            as={motion.div}
            initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.95, y: reduceMotion ? 0 : 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: reduceMotion ? 1 : 0.97, y: reduceMotion ? 0 : 8 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={product.name}
          >
            <P.ModalMedia>
              <P.ModalImage src={product.image} alt={product.name} />
              <P.ModalClose
                type="button"
                onClick={onClose}
                aria-label={t("common.close", { defaultValue: "Close" })}
              >
                <FiX aria-hidden="true" />
              </P.ModalClose>
            </P.ModalMedia>

            <P.ModalBody>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <P.ModalTitle>{product.name}</P.ModalTitle>
                <P.ModalPrice>
                  {formatLek(product.price)}
                  {product.unit ? <small>/ {product.unit}</small> : null}
                </P.ModalPrice>
              </div>

              {badges.length > 0 ? (
                <P.TagRow>
                  {badges.map((badge) => (
                    <P.Tag key={badge} $tone="accent">
                      {badge}
                    </P.Tag>
                  ))}
                </P.TagRow>
              ) : null}

              {product.description ? (
                <P.ModalDescription>{product.description}</P.ModalDescription>
              ) : null}

              {(product.harvestDate ||
                product.location ||
                product.traceabilityId) ? (
                <P.ModalDetailRow>
                  {product.harvestDate ? (
                    <>
                      <dt>
                        {t("products.harvest_date", {
                          defaultValue: "Harvested",
                        })}
                      </dt>
                      <dd>{product.harvestDate}</dd>
                    </>
                  ) : null}
                  {product.location ? (
                    <>
                      <dt>{t("products.origin", { defaultValue: "Origin" })}</dt>
                      <dd>{product.location}</dd>
                    </>
                  ) : null}
                  {product.traceabilityId ? (
                    <>
                      <dt>
                        {t("products.traceability", {
                          defaultValue: "Traceability",
                        })}
                      </dt>
                      <dd>{product.traceabilityId}</dd>
                    </>
                  ) : null}
                </P.ModalDetailRow>
              ) : null}

              <P.ModalActions>
                {!isOutOfStock ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <P.QuantityPicker>
                      <button
                        type="button"
                        onClick={() =>
                          setQuantity((prev) => Math.max(1, prev - 1))
                        }
                        disabled={quantity <= 1}
                        aria-label={t("products.decrease", {
                          defaultValue: "Decrease quantity",
                        })}
                      >
                        –
                      </button>
                      <span aria-live="polite">{quantity}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setQuantity((prev) =>
                            hasStock > 0
                              ? Math.min(hasStock, prev + 1)
                              : prev + 1
                          )
                        }
                        disabled={hasStock > 0 && quantity >= hasStock}
                        aria-label={t("products.increase", {
                          defaultValue: "Increase quantity",
                        })}
                      >
                        +
                      </button>
                    </P.QuantityPicker>
                    <Button
                      type="button"
                      variant="primary"
                      iconLeft={<FiShoppingBag aria-hidden="true" />}
                      onClick={handleAddToCart}
                    >
                      {t("products.add_to_cart", {
                        defaultValue: "Add to cart",
                      })}
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" disabled>
                    {t("products.out_of_stock", {
                      defaultValue: "Out of stock",
                    })}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  iconRight={<FiArrowRight aria-hidden="true" />}
                  onClick={handleViewFull}
                >
                  {t("products.view_details", {
                    defaultValue: "View full details",
                  })}
                </Button>
              </P.ModalActions>
            </P.ModalBody>
          </P.ModalFrame>
        </P.ModalOverlay>
      ) : null}
    </AnimatePresence>
  );
};

export default ProductDetailModalRedesigned;
